import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, Loader2, Sparkles, AlertTriangle, CheckCircle2, RefreshCw,
  GraduationCap, ClipboardCheck, TrendingUp, ListPlus, Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AMPEL = {
  rot:    { color: "bg-red-50 text-red-800 border-red-200", dot: "bg-red-600",     label: "Ampel: rot" },
  gelb:   { color: "bg-amber-50 text-amber-900 border-amber-200", dot: "bg-amber-400", label: "Ampel: gelb" },
  gruen:  { color: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "bg-emerald-500", label: "Ampel: grün" },
  orange: { color: "bg-orange-50 text-orange-900 border-orange-200", dot: "bg-orange-500", label: "Ampel: orange" },
};

const NOTE_COLORS = {
  A: "bg-emerald-500 text-white",
  B: "bg-amber-400 text-amber-950",
  C: "bg-orange-500 text-white",
  D: "bg-red-600 text-white",
};

const PRIO_COLORS = {
  hoch: "bg-red-100 text-red-800 border-red-200",
  mittel: "bg-amber-100 text-amber-900 border-amber-200",
  niedrig: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

/**
 * MdSimulationModal — startet eine KI-basierte MD-Prüfsimulation für eine Mappe.
 * Beim Öffnen wird der letzte Report geladen (falls vorhanden), sonst zeigt der
 * Modal einen CTA "Simulation starten".
 */
export default function MdSimulationModal({ mid, patientName, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [addedIdxs, setAddedIdxs] = useState({}); // {i: true} once in plan
  const [addingIdx, setAddingIdx] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/patientenmappe/${mid}/md-simulation`, { withCredentials: true });
        if (data.exists) setReport(data);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, [mid]);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/patientenmappe/${mid}/md-simulation`,
        {},
        { withCredentials: true },
      );
      setReport(data);
      setAddedIdxs({});
      toast.success("MD-Simulation abgeschlossen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Simulation fehlgeschlagen.");
    } finally {
      setRunning(false);
    }
  };

  const addToPlan = async (idx, emp) => {
    setAddingIdx(idx);
    try {
      await axios.post(
        `${API}/fortbildungsplan`,
        {
          mappe_id: mid,
          report_id: report?.report_id,
          titel: emp.titel,
          zielgruppe: emp.zielgruppe,
          prioritaet: emp.prioritaet,
          begruendung: emp.begruendung,
        },
        { withCredentials: true },
      );
      setAddedIdxs((prev) => ({ ...prev, [idx]: true }));
      toast.success("In Fortbildungsplan übernommen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übernahme fehlgeschlagen.");
    } finally {
      setAddingIdx(null);
    }
  };

  const total = report
    ? (report.notenverteilung?.A || 0) + (report.notenverteilung?.B || 0) + (report.notenverteilung?.C || 0) + (report.notenverteilung?.D || 0)
    : 0;

  const ampel = report ? (AMPEL[report.gesamt_ampel] || AMPEL.gelb) : null;

  return (
    <div
      data-testid="md-simulation-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-espresso-deep/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-hover w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-cream-300 bg-cream-50">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-brand-darkred mb-1">
              <ClipboardCheck className="h-3 w-3" /> MD-Prüfsimulation · KI-Audit
            </div>
            <h2 className="font-display text-2xl text-sand-800 leading-tight">{patientName}</h2>
            {report?.created_at && (
              <p className="text-[11.5px] text-sand-500 mt-0.5">
                Erstellt {new Date(report.created_at).toLocaleString("de-DE")}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-sand-400 hover:text-sand-800"
            aria-label="Schließen"
            data-testid="md-modal-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6">
          {loading && (
            <div className="flex items-center justify-center py-16 text-sand-600 text-[14px]">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade …
            </div>
          )}

          {!loading && !report && (
            <div className="text-center py-10">
              <Sparkles className="h-10 w-10 text-brand-darkred mx-auto mb-3" strokeWidth={1.5} />
              <h3 className="font-display text-xl text-sand-800 mb-2">Noch keine Simulation vorhanden</h3>
              <p className="text-[14px] text-sand-600 max-w-md mx-auto mb-5 leading-relaxed">
                Claude Sonnet 4.6 spielt eine MDK-Prüfung nach QPR-2026 durch, bewertet alle
                7 Prüfbereiche mit A/B/C/D und schlägt konkrete Fortbildungen für dein Team vor.
              </p>
              <button
                data-testid="btn-md-run"
                onClick={run}
                disabled={running}
                className="inline-flex items-center gap-2 rounded-full bg-brand-darkred text-cream-50 px-5 py-2.5 text-[14px] font-semibold hover:bg-brand-red disabled:opacity-60"
              >
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                MD-Simulation starten
              </button>
              <p className="text-[11px] text-sand-500 mt-3">
                Dauert ~15-30 Sek · max. 3 Simulationen pro Tag
              </p>
            </div>
          )}

          {!loading && report && (
            <div className="space-y-6">
              {/* Ampel + Notenverteilung */}
              <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4">
                <div className={`rounded-2xl border ${ampel.color} px-5 py-4 flex flex-col items-center justify-center min-w-[160px]`}>
                  <span className={`inline-block h-8 w-8 rounded-full ${ampel.dot} mb-1.5`} />
                  <span className="text-[11px] font-bold uppercase tracking-wider">Gesamt-Ampel</span>
                  <span className="font-display text-2xl mt-0.5" data-testid="md-ampel">
                    {report.gesamt_ampel?.toUpperCase()}
                  </span>
                </div>
                <div className="rounded-2xl bg-cream-50 border border-cream-300 px-5 py-4">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-sand-500 mb-2">
                    QPR-26 Notenverteilung
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {["A", "B", "C", "D"].map((n) => (
                      <div key={n} className="text-center">
                        <div className={`rounded-lg ${NOTE_COLORS[n]} py-2 font-display text-xl tabular-nums`}>
                          {report.notenverteilung?.[n] || 0}
                        </div>
                        <div className="text-[10px] text-sand-600 mt-1 font-semibold">
                          {n} · {n === "A" ? "erfüllt" : n === "B" ? "weitgeh." : n === "C" ? "überw. nicht" : "nicht erfüllt"}
                        </div>
                      </div>
                    ))}
                  </div>
                  {total !== 26 && (
                    <div className="text-[10.5px] text-sand-500 mt-2">
                      (Summe: {total}/26 · KI-Auswertung)
                    </div>
                  )}
                </div>
              </div>

              {/* Gesamt-Kommentar */}
              {report.gesamt_kommentar && (
                <div className="rounded-xl bg-cream-100 border border-cream-300 px-4 py-3">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-brand-darkred mb-1">Prüfer-Kommentar</div>
                  <p className="text-[14px] text-sand-800 leading-relaxed">{report.gesamt_kommentar}</p>
                </div>
              )}

              {/* Bereich-Findings */}
              {report.bereich_findings?.length > 0 && (
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-sand-600 mb-2 flex items-center gap-1.5">
                    <TrendingUp className="h-3.5 w-3.5" /> Findings pro Prüfbereich
                  </div>
                  <div className="space-y-2">
                    {report.bereich_findings.map((bf, i) => (
                      <div
                        key={bf.bereich_id || i}
                        data-testid={`md-bereich-${bf.bereich_id}`}
                        className="rounded-xl border border-cream-300 bg-white px-4 py-3"
                      >
                        <div className="flex items-start justify-between gap-3 mb-1.5">
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] font-bold uppercase tracking-widest text-sand-500">
                              Bereich {bf.bereich_id}
                            </span>
                            <div className="font-semibold text-[14px] text-sand-800 leading-tight">
                              {bf.bereich_titel}
                            </div>
                          </div>
                          <span
                            className={`shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full font-display text-lg ${NOTE_COLORS[bf.note] || "bg-sand-300 text-sand-800"}`}
                            title={`Note ${bf.note}`}
                          >
                            {bf.note}
                          </span>
                        </div>
                        <p className="text-[13.5px] text-sand-700 leading-relaxed">{bf.befund}</p>
                        {bf.kritische_kriterien?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {bf.kritische_kriterien.map((k, ki) => (
                              <span
                                key={ki}
                                className="inline-flex items-center rounded-full bg-red-50 text-red-800 border border-red-200 text-[10.5px] font-semibold px-2 py-0.5"
                              >
                                Kriterium {k}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fortbildungs-Empfehlungen */}
              {report.weiterbildungs_empfehlungen?.length > 0 && (
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-brand-darkred mb-2 flex items-center gap-1.5">
                    <GraduationCap className="h-3.5 w-3.5" /> Weiterbildungs-Empfehlungen für dein Team
                  </div>
                  <div className="space-y-2">
                    {report.weiterbildungs_empfehlungen.map((emp, i) => {
                      const prio = PRIO_COLORS[emp.prioritaet] || PRIO_COLORS.mittel;
                      const isAdded = !!addedIdxs[i];
                      const isAdding = addingIdx === i;
                      return (
                        <div
                          key={i}
                          data-testid={`md-empfehlung-${i}`}
                          className="rounded-xl border border-cream-300 bg-white px-4 py-3"
                        >
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="font-display text-[15px] text-sand-800 leading-tight flex-1">
                              {emp.titel}
                            </div>
                            <span className={`shrink-0 inline-flex items-center rounded-full border ${prio} text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5`}>
                              {emp.prioritaet || "mittel"}
                            </span>
                          </div>
                          {emp.zielgruppe && (
                            <div className="text-[11.5px] text-sand-500 mb-1">
                              Zielgruppe: <span className="font-semibold text-sand-700">{emp.zielgruppe}</span>
                            </div>
                          )}
                          {emp.begruendung && (
                            <p className="text-[13px] text-sand-700 leading-relaxed mb-2">{emp.begruendung}</p>
                          )}
                          <div className="flex items-center justify-end">
                            <button
                              type="button"
                              onClick={() => addToPlan(i, emp)}
                              disabled={isAdding || isAdded}
                              data-testid={`btn-add-plan-${i}`}
                              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                                isAdded
                                  ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                  : "bg-brand-darkred text-cream-50 hover:bg-brand-red disabled:opacity-60"
                              }`}
                            >
                              {isAdding && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                              {!isAdding && isAdded && <Check className="h-3.5 w-3.5" />}
                              {!isAdding && !isAdded && <ListPlus className="h-3.5 w-3.5" />}
                              {isAdded ? "Im Plan" : "In Fortbildungsplan"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && report && (
          <div className="border-t border-cream-300 bg-cream-50 px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[11px] text-sand-500 leading-tight">
              KI-Simulation · nicht ersatzhaft für echte MDK-Prüfung
            </p>
            <div className="flex items-center gap-2">
              <a
                href="/campus/app/fortbildungsplan"
                target="_blank"
                rel="noreferrer"
                data-testid="link-plan"
                className="inline-flex items-center gap-1.5 rounded-full border border-cream-300 bg-white text-sand-800 px-4 py-2 text-[13px] font-semibold hover:border-brand-darkred/40"
              >
                <GraduationCap className="h-3.5 w-3.5" /> Fortbildungsplan öffnen
              </a>
              <button
                data-testid="btn-md-rerun"
                onClick={run}
                disabled={running}
                className="inline-flex items-center gap-1.5 rounded-full border border-cream-300 bg-white text-sand-800 px-4 py-2 text-[13px] font-semibold hover:border-brand-darkred/40 disabled:opacity-60"
              >
                {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Neu simulieren
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
